import { getInitials } from '../../../../utility/Utils'
import { IconBuildingSkyscraper, IconUser, IconUsers } from '@tabler/icons-react'
import { useAgentContext } from './AgentFormContext'
import { IconRobotFace } from '@tabler/icons-react'
import Skeleton from 'react-loading-skeleton'
import { useSelector } from 'react-redux'

const visibilityIconMap: Record<string, React.ReactNode> = {
  Personal: <IconUser size={12} />,
  Team: <IconUsers size={12} />,
  Enterprise: <IconBuildingSkyscraper size={12} />
}

const AgentSummary = () => {
  const {
    assistantName,
    selectedAvatar,
    assistantDescription,
    selectedAccessOption,
    selectedSkills
  } = useAgentContext()

  const { agent } = useSelector((state: any) => state.agentReducer)
  const agentSkills: string[] = agent?.skills || []

  // Helper function to get displayable URL from File or string
  const getAvatarDisplayUrl = (avatar: File | string | null): string => {
    if (!avatar) return ''
    if (typeof avatar === 'string') return avatar
    if (avatar instanceof File) return URL.createObjectURL(avatar)
    return ''
  }

  // Helper function to get predefined Tailwind classes for visibility badge
  const getVisibilityBgClass = (option: string) => {
    switch (option) {
      case 'Personal':
        return 'bg-[var(--color-secondary)]'
      case 'Team':
        return 'bg-[var(--team-agent)]'
      case 'Enterprise':
        return 'bg-[var(--enterprise-agent)]'
      default:
        return 'bg-[var(--color-secondary)]'
    }
  }

  // Note: Using `react-loading-skeleton` for placeholders

  return (
    <div className="sticky top-1">
      <div className="flex items-center justify-end mb-1">
        <h3 className="card-header-text theme-text-subheading">Agent summary</h3>
      </div>

      <div className="pt-0">
        {/* Agent Preview Card */}
        <div className="theme-framed-card rounded-lg relative">
          <div className="absolute -top-12 left-8">
            <div className="w-24 h-24 theme-surface rounded-full flex items-center justify-center border-4 theme-border-surface shadow-sm overflow-hidden">
              {selectedAvatar ? (
                <img
                  src={getAvatarDisplayUrl(selectedAvatar)}
                  alt="Avatar"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : assistantName !== '' ? (
                <div className="w-24 h-24 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-semibold">
                  {assistantName ? getInitials(assistantName).slice(0, 2) : 'AI'}
                </div>
              ) : (
                <div className="rounded-full w-[85px] h-[85px] flex items-center justify-center theme-surface-hover text-white text-[2rem] relative overflow-hidden">
                  <IconRobotFace size={46} color="var(--icon-muted)" stroke={1.5} />
                </div>
              )}
            </div>
          </div>

          <div className="theme-surface px-1 pt-16 rounded-t-lg">
            {assistantName !== '' ? (
              <h2 className="page-header mb-1">{assistantName}</h2>
            ) : (
              <div className="mb-1">
                <Skeleton width="60%" height={24} baseColor="var(--skeleton-base)" />
              </div>
            )}

            {assistantDescription !== '' ? (
              <p className="theme-text-subheading leading-relaxed line-clamp-3 mb-1">
                {assistantDescription}
              </p>
            ) : (
              <div className="mb-1">
                <Skeleton height={12} baseColor="var(--skeleton-base)" />
                <Skeleton height={12} width="95%" baseColor="var(--skeleton-base)" />
                <Skeleton height={12} width="70%" baseColor="var(--skeleton-base)" />
              </div>
            )}
          </div>

          <div className="theme-surface-muted p-1 rounded-b-lg">
            <div className="flex gap-4 mb-1">
              <div className="flex-1">
                <label className="form-label-small uppercase mb-1">Visibility</label>
                <div className="flex items-center gap-2">
                  {selectedAccessOption ? (
                    <span
                      className={`text-white text-sm px-1 py-50 rounded flex items-center gap-50 ${getVisibilityBgClass(
                        selectedAccessOption
                      )}`}>
                      {visibilityIconMap[selectedAccessOption]}
                      {selectedAccessOption}
                    </span>
                  ) : (
                    <Skeleton
                      width={60}
                      height={28}
                      containerClassName="!rounded"
                      baseColor="var(--skeleton-base)"
                    />
                  )}
                </div>
              </div>
              {/* <div className="flex-1">
                  <label className="text-xs font-semibold text-heading mb-1 block uppercase">
                    Sources
                  </label>
                  <div>
                    <div className="text-sm font-medium text-heading mb-50">Centrali</div>
                    <div className="text-xs text-subheading">4 files, 2 products</div>
                  </div>
                </div> */}
            </div>

            {/* Skills Section */}
            {agentSkills.length > 0 && (
              <div className="mb-1">
                <label className="form-label-small uppercase mb-1">Skills</label>
                <div className="flex flex-wrap gap-50">
                  {agentSkills.map((skill: string) => (
                    <span
                      key={skill}
                      className="bg-[var(--gray-200)] text-sm text-[var(--gray-600)] px-1 py-[4px] !rounded-full flex items-center gap-50 capitalize shadow-sm">
                      {skill.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* <div className="mb-1">
                <label className="text-xs font-semibold text-heading mb-1 block uppercase">
                  Skills
                </label>
                <div className="flex items-center gap-50">
                  {selectedSkills && selectedSkills.length > 0 ? (
                    selectedSkills.map((skill: any, index: number) => (
                      <span
                        key={index}
                        className="bg-surface-muted text-sm px-1 py-50 !rounded-full flex items-center gap-50">
                        {skill.label}
                      </span>
                    ))
                  ) : (
                    <SkeletonLoader width="100px" height="30px" className="rounded" />
                  )}
                </div>
              </div> */}

            {/* Plugins Section */}
            <div className="mt-1">
              {/*
                // Skeleton for Plugins label
                <SkeletonLoader width="60px" height="14px" className="mb-2 rounded" />
                // Skeleton for 3 plugin icons
                <div className="flex items-center gap-2">
                  <SkeletonLoader width="32px" height="32px" className="rounded" />
                  <SkeletonLoader width="32px" height="32px" className="rounded" />
                  <SkeletonLoader width="32px" height="32px" className="rounded" />
                </div>
                */}
              {/* <label className="text-xs font-semibold text-subheading mb-2 block uppercase tracking-wider">
                  Plugins
                </label>
                <div className="flex items-center gap-1">
                  <div className=" rounded flex items-center justify-center">
                    <img src={smartdocs} className="w-[40px] h-[40px]" />
                  </div>
                  <div className="rounded flex items-center justify-center">
                    <img src={workday} className="w-[40px] h-[40px]" />
                  </div>
                  <div className="w-10 h-10 bg-blue-600 rounded flex items-center justify-center">
                    <img src={outlook} className="w-[40px] h-[40px]" />
                  </div> 
                </div>*/}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgentSummary
